import * as React from 'react';

import type { WorkItemDecisionCode } from '../../api/work-item-contracts';
import { ApiError } from '@/domains/api';
import { PAGE_SIZE_MAX } from '../../api/oversight-contracts';
import { workItemsApi } from '../../api/work-items-api';
import { refusalMessage } from '../../refusal';
import { transitionRefusal, UNCLOSED_STATES } from '../../work-items';
import {
  DecisionDialog,
  decisionBody,
  NOT_PERMITTED_RAISE
} from '../compliance/work-item-writes';
import { sentence } from './said';

/**
 * Escalating a record from its page (SR-400's AC3, as ruled on the ticket 2026-10-09): the
 * compliance queue's own decision, `escalate`, with the queue's dialog — **its rationale optional**
 * since SR-396 (W2; the owner's Q4 over SR-400's "required"), a blank one left out of the body.
 *
 * **Which item** is read at the press, never from what the page drew: the record's unclosed items
 * now. The first not yet escalated is escalated; with none at all, a manual item is raised on the
 * record — its note the rationale — and escalated in the same press; with every one already
 * escalated, nothing is sent and the dialog says so. **A raised item needs its note**
 * (survey-api still requires one, 1–4,000), so with no open item a blank rationale sends nothing
 * and the dialog stays open saying a note is needed (Q4) — said at the press, because only the
 * read then says whether an item has to be raised; no note is written for the person. An
 * escalated item lands in the queue's Escalated view, the spec's secondary-review set; nobody is
 * assigned (ruling 1 — My tasks waits for Veritant to name the final reviewer).
 *
 * Two calls where an item is raised, and not atomic: a raise that lands under a refused decision
 * leaves an open item, which the re-read draws and the sentence says. Every answer re-reads the
 * page (`reread`), so the summary's compliance line and Work items follow. **What a failure says
 * is the step it failed at** (`step`): a read that failed sent nothing, a raise left unanswered may
 * have made an item, a decision left unanswered after a raise did make one — one sentence for all
 * three told a reader to check for a write that was never sent. A 409 `invalid-transition` (the
 * item decided by somebody else since the read) is said in survey-api's `reason`, the queue's way.
 */
export function useEscalate(
  participantId: string,
  about: string,
  reread: () => Promise<boolean>,
  /** `responses.annotate` — raising an item, where the record holds none to escalate. */
  canRaise: boolean
): { open: () => void; dialog: React.ReactNode } {
  const [decision, setDecision] = React.useState<Nullable<WorkItemDecisionCode>>(null);

  const escalate = async (rationale: string): Promise<Nullable<string>> => {
    let step: 'read' | 'raise' | 'decide' = 'read';
    let raised = false;
    try {
      const unclosed = await workItemsApi.listWorkItems({
        participantId,
        state: [...UNCLOSED_STATES],
        pageSize: PAGE_SIZE_MAX
      });
      let target = unclosed.items.find((row) => row.state !== 'escalated')?.id;
      if (target === undefined && unclosed.items.length > 0) {
        return 'Every open item on this record is already escalated — nothing was sent.';
      }
      if (target === undefined && !canRaise) {
        return (
          'This record has no open item to escalate, and your Team Portal roles do not include ' +
          `${NOT_PERMITTED_RAISE} — nothing was sent.`
        );
      }
      if (target === undefined && rationale === '') {
        return (
          'This record has no open item to escalate. A new one needs a note — write the ' +
          'rationale, and it is raised with it.'
        );
      }
      if (target === undefined) {
        step = 'raise';
        const row = await workItemsApi.raiseWorkItem(participantId, {
          subject: 'Escalated from the record page',
          note: rationale
        });
        raised = true;
        target = row.id;
      }
      step = 'decide';
      await workItemsApi.decideWorkItems(decisionBody([target], 'escalate', rationale));
      void reread();
      return null;
    } catch (error) {
      void reread();
      // An answer that never arrived is no refusal: the write may have landed (the hub's rule).
      if (!(error instanceof ApiError) || error.status >= 500) {
        if (step === 'read') {
          return 'The record’s open items could not be read — nothing was sent. Try again.';
        }
        if (step === 'raise') {
          return (
            'The answer did not arrive — an item may have been raised on this record, not yet ' +
            'escalated. The page was read again: check the compliance line before escalating again.'
          );
        }
        return (
          (raised
            ? 'An item was raised on this record, but the answer to its escalation did not ' +
              'arrive — it may have been escalated.'
            : 'The answer did not arrive — the escalation may have gone through.') +
          ' The page was read again: check the compliance line before escalating again.'
        );
      }
      if (step === 'read') {
        const said = refusalMessage(error, 'The record’s open items could not be read');
        return `${sentence(said)} Nothing was sent.`;
      }
      const refused = transitionRefusal(error);
      const said = refused
        ? sentence(refused.reason ?? 'The item’s state does not take that decision')
        : refusalMessage(error, 'The escalation was refused');
      return raised
        ? `An item was raised on this record, but its escalation was refused: ${said}`
        : said;
    }
  };

  return {
    open: () => setDecision('escalate'),
    dialog: (
      <DecisionDialog
        decision={decision}
        about={about}
        onClose={() => setDecision(null)}
        onDecide={async (_, rationale) => {
          const said = await escalate(rationale);
          if (said === null) setDecision(null);
          return said;
        }}
      />
    )
  };
}
